import { useRoute } from 'vue-router';
import { useUrl } from '@/composables/useUrl';
import { useReportParams } from '@/composables/useReportParams';
import { useTableQuery } from '@/composables/useTableQuery';
import { collectReportFilters } from '@/views/reports/report-filters-collect';
import {
  buildInitialReportFilters,
  verifyReportQuery,
} from '@/views/reports/report-filters-build';

export const useReportFilters = () => {
  const route = useRoute();
  const url = useUrl();
  const reportParamsApi = useReportParams();
  const tableQueryApi = useTableQuery();

  const getReportFilters = () => {
    return collectReportFilters(
      url.reportQuery() as Record<string, unknown>,
      (url.reportDecoded.value || null) as Record<string, unknown> | null,
    );
  };

  const resolveGroupBy = (type?: string) => {
    return type || (route.params.type as string);
  };

  const getInitialFilters = (options: {
    grouping: string | null | undefined;
    type?: string;
  }) => {
    return buildInitialReportFilters({
      routeData: route.params.data,
      groupBy: resolveGroupBy(options.type),
      grouping: options.grouping,
      reportParams: reportParamsApi.getReportParams() as Record<string, unknown>,
    });
  };

  const getFilters = (options: {
    grouping: string | null | undefined;
    type?: string;
  }) => {
    const filters = {
      ...getInitialFilters(options),
      ...getReportFilters(),
    };
    const current = tableQueryApi.tableQueryForCurrentTab.value;

    if ((!current.from || !current.to) && filters.from && filters.to) {
      tableQueryApi.patchTablePeriodForCurrentTab({
        from: String(filters.from),
        to: String(filters.to),
        date: String(filters.date || 'today'),
        timezone: filters.timezone ? String(filters.timezone) : undefined,
      });
    }

    return filters;
  };

  const verifyReportQueryForSelection = (
    query: Record<string, unknown>,
    treeLevel: number,
    selectedMetrics: string[],
  ) => {
    return verifyReportQuery({
      query,
      treeLevel,
      selectedMetrics,
      removeTableColumnFilter: tableQueryApi.removeTableColumnFilter,
    });
  };

  return {
    reportQuery: url.reportQuery,
    isDashboard: url.isDashboard,
    basicType: url.basicType,
    currentReportType: url.currentReportType,
    isReport: url.isReport,
    getGroupByMetric: url.getGroupByMetric,
    drilldownForward: url.drilldownForward,
    getReportFilters,
    getInitialFilters,
    getFilters,
    verifyReportQueryForSelection,
    resolveGroupBy,
    getReportParams: reportParamsApi.getReportParams,
    groupBy: () => route.params.type,
  };
};
